import { canonicalSchemaJson, createSchemaAjv, schemaId, wrapSchemaValidator } from './schemaCompilation.js';
/** Constructs isolated state; callers determine whether it remains local or is retained. */
export function createLocalSchemaRegistry(options = {}) {
    const ajv = createSchemaAjv(options.annotationKeywords, options.metaSchemas, options.annotationKeywordSchemas);
    const registered = new Map();
    const byReference = new Map();
    const byContent = new Map();
    const byObject = new WeakMap();
    const registry = {
        validateSchema(schema) {
            const valid = ajv.validateSchema(schema);
            if (typeof valid !== 'boolean')
                throw new Error('Asynchronous schemas are not supported.');
            return { valid, issues: valid ? [] : (ajv.errors ?? []).map(error => ({ ...error })) };
        },
        register(schema) {
            const id = schemaId(schema);
            if (!id)
                throw new Error('Registered schemas require an $id.');
            const content = canonicalSchemaJson(schema);
            const existing = registered.get(id);
            if (existing === content)
                return;
            if (existing !== undefined)
                throw new Error(`Schema '${id}' is already registered with different content.`);
            ajv.addSchema(schema);
            registered.set(id, content);
        },
        validator(reference) {
            const cached = byReference.get(reference);
            if (cached)
                return cached;
            const hash = reference.indexOf('#');
            const id = hash < 0 ? reference : reference.slice(0, hash);
            if (!registered.has(id))
                throw new Error(`Schema '${id}' is not registered.`);
            const validate = ajv.getSchema(reference);
            if (!validate)
                throw new Error(`Schema reference '${reference}' is unavailable.`);
            const validator = wrapSchemaValidator(ajv, validate, id);
            byReference.set(reference, validator);
            return validator;
        },
        compile(schema) {
            const identical = typeof schema === 'object' ? byObject.get(schema) : undefined;
            if (identical)
                return identical;
            const content = canonicalSchemaJson(schema);
            let validator = byContent.get(content);
            if (!validator) {
                const id = schemaId(schema);
                if (id) {
                    registry.register(schema);
                    validator = registry.validator(id);
                }
                else {
                    validator = wrapSchemaValidator(ajv, ajv.compile(schema), undefined);
                }
                byContent.set(content, validator);
            }
            if (typeof schema === 'object')
                byObject.set(schema, validator);
            return validator;
        },
    };
    return registry;
}
